import React, {useEffect, useRef, useState} from 'react';
import Table from 'react-bootstrap/Table';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import EditIcon from '@mui/icons-material/Edit';
import { CancelRounded, Check } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import useAxiosProtect from '../../../../../../hooks/useAxiosProtect';
import { FormHelperText } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import Form from 'react-bootstrap/Form';
import SensorButton from '../sensors_v2/SensorButton';
import SensorModal from '../sensors_v2/SensorModal';
import SensorCalibrationHistory from '../sensors_v2/SensorCalibrationHistory';
import SensorTelemetryIcons from '../sensors_v2/SensorTelemetryIcons';
import ManualReadingsModal from '../sensors_v2/ManualReadingsModal';
import useAuth from '../../../../../../hooks/useAuth';

const CentricaRegistry = props => {
    const {auth} = useAuth();
    const floatRegex = /^(|\d*\.?\d+|\d+\.)$/;
    const axiosProtect = useAxiosProtect();

    const [sensorForHistory, setSensorForHistory] = useState(null);

    const [siteNameEditOn, setSiteNameEditOn] = useState(false);
    var centricaName = useRef('');
    const [centricaFieldValue, setCentricaFieldValue] = useState('');
    const [centricaFieldErr, setCentricaFieldErr] = useState('');

    const [sensors, setSensors] = useState([]);
    const [noSensorMessage, setNoSensorMessage] = useState('');

    const [ctModalState, setCTModalState] = useState('');
    const [ctModalData, setCTModalData] = useState(null);
    const [showManualReadings, setShowManualReadings] = useState(false);

    const [isCopied, setIsCopied] = useState(false);

    const fileInputRef = useRef(null);
    const selectedSensorRef = useRef(null);
    var init = useRef(true);
    const siteID = window.location.href.split('/')[window.location.href.split('/').length - 1];

    useEffect(() => {
        if(init.current){
            init.current = false;

            const getCentricaData = async () => {
                try{
                    let response = await axiosProtect.get(`/v2s/v2-sensors?site_id=${siteID}&utility=electricity`);

                    if(response.status !== 200){
                        setNoSensorMessage('An error occurred');
                        return
                    }

                    if(response.data.sensors.length === 0){
                        setNoSensorMessage('No sensors available');
                        return
                    }

                    response.data.sensors.sort((a, b) => {
                        let nameA = a.name.toLowerCase();
                        let nameB = b.name.toLowerCase();

                        return nameA < nameB ? -1 : 1;
                    });

                    let ctCopy = [];

                    for(let i = 0;i<response.data.sensors.length;++i){
                        let ct = response.data.sensors[i];

                        if(!isNaN(ct.calibration_factor) && typeof ct.calibration_factor === 'number'){
                            ct.calibrationEditOn = false;
                            ct.calibrationValue = ct.calibration_factor.toString();
                            ct.calibrationError = '';
                        }

                        ctCopy.push(ct);
                    }

                    setSensors([...ctCopy]);

                }catch(err){
                    setNoSensorMessage('An error occurred');
                    return;
                }
            }

            getCentricaData();
        }
    }, []);

    useEffect(() => {
        props.updateSensorCount('electricity', sensors.length);
    }, [sensors]);

    const handleClickToggleEdit = () => {
        if(siteNameEditOn){
            setCentricaFieldValue(centricaName.current);
        }

        setCentricaFieldErr('');
        setSiteNameEditOn(edit => !edit);
    }

    const handleMouseDownEdit = (event) => {
        event.preventDefault();
    };

    const handleClickToggleEditCalibration = unit => {
        let unitCopy = {...unit};

        if(unitCopy.calibrationEditOn){
            unitCopy.calibrationEditOn = false;
            unitCopy.calibrationError = '';
            unitCopy.calibrationValue = unitCopy.calibration_factor.toString();
        }else{
            unitCopy.calibrationEditOn = true;
        }

        let sensorCopy = [];
        for(let i = 0;i<sensors.length;++i){
            if(sensors[i].id !== unitCopy.id) sensorCopy.push(sensors[i]);
            else sensorCopy.push(unitCopy);
        }

        setSensors([...sensorCopy]);
    }

    const updateCentricaName = async () => {
        setCentricaFieldErr('Centrica configuration is read-only');
    }

    const normalizeSensor = sensor => ({
        ...sensor,
        calibrationEditOn: false,
        calibrationValue: sensor.calibration_factor?.toString() || '',
        calibrationError: ''
    });

    const addCT = ct => {
        const normalizedCT = normalizeSensor(ct);
        let insertIndex = sensors.findIndex(sensor => sensor.name.localeCompare(
            normalizedCT.name, undefined, {sensitivity: 'base'}
        ) > 0);

        if(insertIndex === -1){
            setSensors(prev => [...prev, normalizedCT]);
            return;
        }

        setSensors(prev => [
            ...prev.slice(0, insertIndex),
            normalizedCT,
            ...prev.slice(insertIndex),
        ]);
    }

    const editCT = ct => {
        const normalizedCT = normalizeSensor(ct);
        let tempCopy = [...sensors];

        for(let i = 0;i<tempCopy.length;++i){
            if(tempCopy[i].id === normalizedCT.id){
                tempCopy[i] = normalizedCT;
                break;
            }
        }

        setSensors([...tempCopy]);
    }

    const closeModal = () => {
        setCTModalState('');
        setCTModalData(null);
    }

    const handleFileChange = async (e) => {
        const sensor = selectedSensorRef.current;
        const file = e.target.files[0];
        let site_id = parseInt(window.location.href.split('/').slice(-1));
        if (file) {
            if (file.size > 5000000) {
                e.target.value = null;
                return;
            }
            const formData = new FormData();
            formData.append("file", file);
            formData.append("upload_preset", "stewwi");
            const res = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY}/image/upload`, {
                method: "POST",
                body: formData
            });

            const uploaded = await res.json();

            if(!res.ok || !uploaded.url){
                throw new Error(uploaded.error?.message || 'Unable to upload image');
            }

            let payload = {
                file: uploaded.url,
                site_id,
                sensor_id: sensor.sensor_id || sensor.id,
            };

            axiosProtect.put('/v2s/v2-sensors/image', payload).then(response => {
                editCT(response.data.sensor);
            }).catch(err => console.log(err));
            e.target.value = null;
        }
    };

    const triggerFileUpload = (sensor) => {
        selectedSensorRef.current = sensor;
        fileInputRef.current.click();
    }

    const copyCentricaURL = () => {
        navigator.clipboard.writeText(`https://rmisbalancer.herokuapp.com/ct/centrica/ct/sync?id=${siteID}`);
        setIsCopied(true);

        setTimeout(() => {
            setIsCopied(false);
        }, 1500);
    }

    const updateCalibrationFactor = unit => {
        let unitCopy = {...unit};
        let newFactor = parseFloat(unitCopy.calibrationValue);
        let hasErrors = false;

        if(isNaN(newFactor)){
            hasErrors = true;
            unitCopy.calibrationError = 'Invalid input';


        }else if(newFactor <= 0){
            hasErrors = true;
            unitCopy.calibrationError = 'Must be > 0';
        }

        if(hasErrors){
            let sensorCopy = [];
            for(let i = 0;i<sensors.length;++i){
                if(sensors[i].id !== unitCopy.id) sensorCopy.push(sensors[i]);
                else sensorCopy.push(unitCopy);
            }

            setSensors([...sensorCopy]);
            return;
        }

        axiosProtect.put('/v2s/v2-sensors/calibrate', {
            new_factor: parseFloat(unitCopy.calibrationValue),
            sensor_id: unitCopy.sensor_id || unitCopy.id,
            site_id: unitCopy.site_id
        }).then(response => {
            setSensors(prevUnits =>
                prevUnits.map(item =>
                    item.id === unitCopy.id ? { 
                        ...item, 
                        calibrationValue: response.data.sensor.calibration_factor.toString(),
                        calibrationEditOn: false,
                        calibrationError: '',
                        calibration_factor: response.data.sensor.calibration_factor
                    } : item
                )
            );
        }).catch(error => {
            setSensors(prevUnits =>
                prevUnits.map(item =>
                    item.id === unitCopy.id ? { 
                        ...item, 
                        calibrationValue: unitCopy.calibration_factor.toString(),
                        calibrationEditOn: false,
                        calibrationError: '',
                        calibration_factor: unitCopy.calibration_factor
                    } : item
                )
            );
        });
    }

    const activateModal = (state, sensor = null) => {
        if(sensor?.supplier_name === 'Centrica' && state !== 'images') return;
        setCTModalData(sensor);
        setCTModalState(state);
    }

    const deleteSensor = sensorID => {
        setSensors(prev => prev.filter(sensor => sensor.id !== sensorID));
    }

    const applyToggledSensor = sensor => {
        setSensors(prev => prev.map(existing => existing.id === sensor.id
            ? {...existing, active: sensor.active}
            : existing));
    }

    const toggleSensor = sensor => {
        if(sensor.supplier_name === 'Centrica') return;

        axiosProtect.put('/v2s/v2-sensors/toggle-active', {
            sensor_id: sensor.sensor_id || sensor.id,
            site_id: sensor.site_id
        }).then(response => applyToggledSensor(response.data.sensor))
            .catch(error => console.log(error));
    }

    const requestToggleSensor = sensor => {
        if(sensor.supplier_name === 'Centrica') return;
        if(sensor.active) activateModal('toggle', sensor);
        else toggleSensor(sensor);
    }

    return(
        <div>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{display: 'none'}}/>

            {
                ctModalState !== '' && 
                <SensorModal
                    modalState={ctModalState}
                    closeModal={closeModal}
                    utility="electricity"
                    sensorToEdit={ctModalState === 'edit' ? ctModalData : null}
                    sensorToDelete={ctModalState === 'delete' ? ctModalData : null}
                    sensorToToggle={ctModalState === 'toggle' ? ctModalData : null}
                    sensorToImage={ctModalState === 'image' ? ctModalData : null}
                    imageReadOnly={ctModalData?.supplier_name === 'Centrica'}
                    triggerFileUpload={triggerFileUpload}
                    addSensor={addCT}
                    editSensor={editCT}
                    deleteSensor={deleteSensor}
                    toggleSensor={applyToggledSensor}
                />
            }

            <SensorCalibrationHistory selectedSensor={sensorForHistory} setSelectedSensor={setSensorForHistory}/>
            <ManualReadingsModal show={showManualReadings} closeModal={() => setShowManualReadings(false)} siteID={siteID} utility="electricity"/>

            <div style={{display:'none'}}>
            {(centricaFieldValue !== '' && centricaFieldErr === '' && sensors.length > 0 && !siteNameEditOn) && <>
                <FormControl style={{width:'40%', marginLeft:'28%'}} variant="outlined">
                    <OutlinedInput
                        readOnly
                        variant="filled"
                        value={`https://rmisbalancer.herokuapp.com/ct/centrica/ct/sync?id=${siteID}`}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton style={{fontSize:'1rem'}} onClick={copyCentricaURL} edge="end">
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </IconButton>
                            </InputAdornment>
                        }
                    />

                    <FormHelperText>
                        Create export job and use this URL when all sensors have been added
                    </FormHelperText>
                </FormControl>
                <br/>
                <br/>
            </>}
            </div>
            
            <div style={{display:'flex', flexDirection: 'row', display:'none'}}>
                <div style={{flex:'0.5'}}></div>

                <div style={{flex:'1'}}>
                    <IconButton style={{height:'100%', width:'50%'}} onClick={() => setCTModalState('add')}>
                        <AddCircleIcon className="text-brand"/>
                    </IconButton>
                </div>

                <FormControl sx={{ flex:'6'}} variant="outlined">
                    <InputLabel htmlFor="centrica-site-name-field">Centrica site name</InputLabel>
                    <OutlinedInput
                        id="centrica-site-name-field"
                        disabled={!siteNameEditOn}
                        value={centricaFieldValue}
                        error={centricaFieldErr !== ''}
                        helperText={centricaFieldErr}
                        onChange={e => setCentricaFieldValue(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                {siteNameEditOn ? 
                                    <>
                                    <IconButton onClick={updateCentricaName}>
                                        <Check/>
                                    </IconButton>
                                    <IconButton edge="end" onClick={handleClickToggleEdit}>
                                        <CancelRounded/>
                                    </IconButton>
                                    </> 
                                    :
                                    <IconButton onClick={handleClickToggleEdit} onMouseDown={handleMouseDownEdit} edge="end">
                                        <EditIcon />
                                    </IconButton>
                                }
                            </InputAdornment>
                        }
                        label="Centrica site name"
                    />
                    {centricaFieldErr !== '' && <FormHelperText error>
                        {centricaFieldErr}
                    </FormHelperText>}
                </FormControl>

                <div style={{flex:'0.5'}}></div>

                <div style={{flex:'8'}}>
                    <small style={{margin:'0'}}>
                        'Centrica site name' refers to the name of the site registered on <a href='https://www.powerradar.energy/' target='_blank'>Power Radar</a>. 
                        Copy+paste the name to avoid discrepancies.
                    </small>
                </div>

                <div style={{flex:'0.5'}}></div>
            </div>

            <br/>

            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <SensorButton label="Add electricity sensor" utility="electricity" method="add" clickAction={() => activateModal('add')}/>
                {auth.role === process.env.REACT_APP_ES_ADMIN && <SensorButton label="Add manual readings" method="add" clickAction={() => setShowManualReadings(true)}/>}
            </div>
             
            <Table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Voltage</th>
                        <th>Power factor</th>
                        <th>Supplier</th>
                        <th>Device name/DevEUI</th>
                        <th>Calibration factor</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {(noSensorMessage !== '' && sensors.length === 0) && <td colSpan={7}>
                        <div style={{backgroundColor:'lightgrey', height:'10vh', display:'flex', justifyContent:'center', alignItems:'center'}}>
                            <p style={{textAlign:'center'}}>{noSensorMessage}</p>
                        </div>
                    </td>}

                    {sensors?.map((ct, i) => (
                        <tr>
                            <td>
                                {ct.name}
                                <SensorTelemetryIcons battery={ct.battery} rssi={ct.rssi} snr={ct.snr}/>
                            </td>
                            <td>
                                <Form.Check
                                    type="switch"
                                    id={`centrica-ct-active-switch-${i}`}
                                    className='centrica-table-switch'
                                    disabled={ct.supplier_name === 'Centrica'}
                                    onChange={() => requestToggleSensor(ct)}
                                    label={ct.active ? 'active' : 'disabled'}
                                    checked={ct.active}
                                />
                            </td>
                            <td>{ct.voltage}</td>
                            <td>{ct.assumed_power_factor}</td>
                            <td>{ct.supplier_name}</td>
                            <td>{
                                ct.supplier_name === 'Milesight' ? 
                                ct.deveui :
                                ct.supplier_name === 'Offline' ?
                                ct.device_type :
                                ct.device_name ? 
                                ct.device_name :
                                'N/A'
                            }</td>
                            <td>
                                {
                                    (!isNaN(ct.calibration_factor) && typeof ct.calibration_factor === 'number') ? 
                                    <OutlinedInput
                                        id={`elec_calibration_field_${ct.id}`}
                                        disabled={!ct.calibrationEditOn}
                                        value={ct.calibrationValue}
                                        error={ct.calibrationError !== ''}
                                        helperText={ct.calibrationError}
                                        onChange={e => {
                                            if(!floatRegex.test(e.target.value)) return;

                                            setSensors(prevUnits =>
                                                prevUnits.map(item =>
                                                    item.id === ct.id ? { ...item, calibrationValue: e.target.value } : item
                                                )
                                            );
                                        }}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                {ct.calibrationEditOn ? 
                                                    <>
                                                    <IconButton onClick={() => updateCalibrationFactor(ct)}>
                                                        <Check/>
                                                    </IconButton>
                                                    <IconButton edge="end" onClick={() => handleClickToggleEditCalibration(ct)}>
                                                        <CancelRounded/>
                                                    </IconButton>
                                                    </> 
                                                    :
                                                    <IconButton onClick={() => handleClickToggleEditCalibration(ct)} onMouseDown={handleMouseDownEdit} edge="end">
                                                        <EditIcon />
                                                    </IconButton>
                                                }
                                            </InputAdornment>
                                        }
                                    />
                                    :
                                    <span>N/A</span>
                                }
                            </td>
                            <td>
                                {
                                    (!isNaN(ct.calibration_factor) && typeof ct.calibration_factor === 'number') && 
                                    <IconButton style={{height:'fit-parent', width:'auto'}} onClick={() => {
                                        setSensorForHistory(ct);
                                    }}>
                                        <ManageHistoryIcon/>
                                    </IconButton>
                                }

                                {ct.images != null ? (
                                    <IconButton onClick={() => activateModal('image', ct)}>
                                        <ImageIcon style={{fill: "#0872B6"}}/>
                                    </IconButton>
                                ) : (
                                    ct.supplier_name !== 'Centrica' && <IconButton onClick={() => triggerFileUpload(ct)}>
                                        <AddPhotoAlternateIcon style={{fill: "#0872B6"}}/>
                                    </IconButton>
                                )}

                                {
                                    ct.supplier_name !== 'Centrica' &&
                                    <SensorButton label="Delete electricity sensor" utility="electricity" method="delete" clickAction={() => activateModal('delete', ct)}/>
                                }

                                {
                                    ct.supplier_name !== 'Centrica' && 
                                    <SensorButton label="Edit electricity sensor" utility="electricity" method="edit" clickAction={() => activateModal('edit', ct)}/>
                                }
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

export default CentricaRegistry;
